import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from 'components/ui/dialog'
import { cn } from 'lib/utils'

import { LAYOUTS } from '../constants'
import { LAYOUT_SPECS } from '../layouts'
import type { SlideLayout } from '../types/presentation.types'
import { makeSlide } from '../utils/deckOps'
import { SlideRenderer } from './SlideRenderer'
import { SlideStage } from './SlideStage'

/**
 * Choose a layout for a new slide (MYD-495). Each option is drawn by the real
 * `SlideRenderer` with its regions shaded, so what you pick is exactly what
 * you get.
 */
interface LayoutPickerDialogProps {
	open: boolean
	onClose: () => void
	onPick: (layout: SlideLayout) => void
	title?: string
	current?: SlideLayout
}

const previewSlide = (layout: SlideLayout) => ({ ...makeSlide(`preview-${layout}`, layout), title: layout === 'title' || layout === 'section' ? LAYOUT_SPECS[layout].label : 'Slide title' })

export function LayoutPickerDialog({ open, onClose, onPick, title = 'Add a slide', current }: LayoutPickerDialogProps) {
	return (
		<Dialog open={open} onClose={onClose}>
			<DialogContent className="sm:max-w-[760px]">
				<DialogHeader>
					<DialogTitle>{title}</DialogTitle>
					<DialogDescription>Pick a layout. You place elements in its areas.</DialogDescription>
				</DialogHeader>
				<div className="grid grid-cols-2 gap-4 p-5 sm:grid-cols-4">
					{LAYOUTS.map(layout => (
						<button
							key={layout}
							type="button"
							onClick={() => onPick(layout)}
							className={cn(
								'group rounded-lg border p-2 text-left transition-colors hover:border-primary hover:bg-primary/5',
								current === layout ? 'border-primary' : 'border-gray-200',
							)}
						>
							<SlideStage width={152} className="overflow-hidden rounded border border-gray-100">
								<SlideRenderer
									slide={previewSlide(layout)}
									blocks={{}}
									interaction={{ renderEmptyRegion: () => <div className="flex-1 rounded-xl bg-gray-200" /> }}
								/>
							</SlideStage>
							<span className="mt-2 block text-sm font-medium text-gray-900">{LAYOUT_SPECS[layout].label}</span>
							<span className="block text-xs leading-snug text-gray-500">{LAYOUT_SPECS[layout].description}</span>
						</button>
					))}
				</div>
			</DialogContent>
		</Dialog>
	)
}

export default LayoutPickerDialog
